import classNames from 'classnames';
import React, { useMemo } from 'react';
import {
  DivTooltip,
  Interpolate,
  Message,
  ResourceImage,
  Tooltip,
  TooltipBody,
  TooltipHeader,
  Spinner,
} from '@wg/wows-react-uikit';

import { CLANSTARS_REWARD_STATUSES } from '~/constants';
import { isoToFormattedLocalDateTime } from '~/helpers/datetime';
import { t } from '~/helpers/localization';

import styles from './ProgressReward.scss';
import { REWARDS_GOAL_TO_DATA } from '../../../constants';

import type { IClanStarsReward, IClanStarsRewardInfo } from '~/Reducers/ReducerClanStars';

type IRewardProps = {
  accountRewards: IClanStarsReward[];
  className?: string;
  clanstarsCount: number;
  status?: CLANSTARS_REWARD_STATUSES;
  items: IClanStarsRewardInfo[];
  rewardGoal: number;
};

const Reward: React.FC<IRewardProps> = ({ accountRewards, className, rewardGoal, clanstarsCount, items, status }) => {
  const rewardClaimedAt = useMemo(() => {
    for (const reward of accountRewards) {
      if (reward.clanstarsCount === rewardGoal && reward.claimedAt) {
        return isoToFormattedLocalDateTime(reward.claimedAt);
      }
    }
    return null;
  }, [accountRewards, rewardGoal]);

  const rewardData = REWARDS_GOAL_TO_DATA[rewardGoal];
  if (!rewardData) {
    return null;
  }

  const amount = items[0].amount;
  const { clan, description, resource, title } = rewardData;

  const isObtained = status === CLANSTARS_REWARD_STATUSES.COMPLETED;
  const isPending = status === CLANSTARS_REWARD_STATUSES.PENDING;

  return (
    <DivTooltip
      className={className}
      tooltipBody={
        <Tooltip>
          <div className={styles.tooltipWrapper}>
            <TooltipHeader isBold={true}>
              <Interpolate
                className={styles.header}
                str={t('Награда за получение %(star)s')}
                star={
                  <>
                    <span className={styles.count}>{rewardGoal}</span>
                    <div className={styles.star} />
                  </>
                }
                noWrap
              />
            </TooltipHeader>
            <TooltipBody>
              <div className={styles.tooltipBodyWrapper}>
                <ResourceImage {...resource} amount={amount} />
                <div className={styles.tooltipDescriptionWrapper}>
                  <div className={styles.tooltipHeader}>{title}</div>
                  <div className={styles.tooltipType}>{description}</div>
                </div>
              </div>
            </TooltipBody>
            {!!clan && (
              <TooltipBody>
                <Message type="error" style={'inline'}>
                  {t('Начисляется в казну клана')}
                </Message>
              </TooltipBody>
            )}
            {!isObtained ? (
              <>
                <TooltipBody>
                  <div className={styles.tooltipFooter}>
                    {t('Нужно еще:')}
                    <div className={styles.countStars}>
                      <span className={styles.count}>{rewardGoal - clanstarsCount}</span>
                      <div className={styles.star} />
                    </div>
                  </div>
                </TooltipBody>
                <TooltipBody>{t('Выполняйте задачи в составе отряда и получайте звезды.')}</TooltipBody>
              </>
            ) : rewardClaimedAt ? (
              <TooltipBody>
                <div className={styles.tooltipFooterGot}>
                  {t('Получено')}
                  <p style={{ color: '#FFCC66' }}>{rewardClaimedAt}</p>
                </div>
              </TooltipBody>
            ) : null}
          </div>
        </Tooltip>
      }
    >
      <div className={styles.reward}>
        <ResourceImage
          className={classNames(styles.resourceImage, { [styles.isObtained]: isObtained || isPending })}
          classNameAmountBadge={styles.amountBadge}
          {...resource}
          amount={amount}
        />
        {isObtained && <span className={styles.isObtainedTick} />}
        {isPending && (
          <div className={styles.centeredIcon}>
            <Spinner />
          </div>
        )}
      </div>
    </DivTooltip>
  );
};

export default React.memo(Reward);
